import {
  Button,
  EmailField,
  FormFeedback,
  PasswordField,
} from '@sparemin/blockhead';
import React from 'react';
import { useAuth } from 'ui/context';
import AuthNavLink from '../AuthNavLink';
import AuthSpacer from '../AuthSpacer';
import useLoginForm, { UseLoginFormConfig } from './useLoginForm';

export interface LoginFormProps
  extends Pick<
    UseLoginFormConfig,
    'onAuthenticateSuccess' | 'onError' | 'onSubmit'
  > {
  className?: string;
  /**
   * When true, form inputs and submit button are disabled
   */
  isDisabled?: boolean;
  /**
   * Called when a user clicks the "forgot password" link
   */
  onForgotPasswordPress?: () => void;
}

/**
 * A form that handles username/password login
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  className,
  isDisabled,
  onError,
  onForgotPasswordPress,
  onAuthenticateSuccess,
  onSubmit,
}) => {
  const { features } = useAuth();

  const {
    hasInputError,
    isLoading,
    onSubmit: handleSubmit,
    register,
    formState: { errors },
    validationState,
  } = useLoginForm({ onAuthenticateSuccess, onError, onSubmit });

  const supportsForgotPassword = features.includes('forgot-password');

  const disabled = isDisabled || isLoading;

  return (
    <AuthSpacer as="form" className={className} onSubmit={handleSubmit}>
      <EmailField
        aria-label="email field"
        autoComplete="email"
        errorMessage={errors.email?.message}
        fluid
        isDisabled={disabled}
        placeholder="Email"
        validationState={validationState.email}
        {...register('email')}
      />
      <PasswordField
        autoComplete="current-password"
        errorMessage={errors.password?.message}
        fluid
        isDisabled={disabled}
        validationState={validationState.password}
        {...register('password')}
      />
      {supportsForgotPassword && (
        <AuthNavLink
          appearance="link"
          onPress={onForgotPasswordPress}
          to="forgot-password"
          typography="heading"
          level={2}
        >
          I forgot my password
        </AuthNavLink>
      )}
      {errors.unregisteredError && (
        <FormFeedback>{errors.unregisteredError.message}</FormFeedback>
      )}
      <Button isDisabled={hasInputError || disabled} fluid type="submit">
        log in
      </Button>
    </AuthSpacer>
  );
};

export default LoginForm;
